import { z } from 'zod';

import type { components } from './schema';

/**
 * Runtime validation of every response body (ARCHITECTURE §3 Validation:
 * "Pydantic on the api; Zod on the web from the same OpenAPI").
 *
 * Each schema is **annotated with the generated type**, so `pnpm gen:api`
 * regenerating `schema.d.ts` after an api change makes `tsc` fail here rather
 * than letting a screen read a field the api stopped sending.
 */

type Schemas = components['schemas'];

/** `MeOut.user` / `SessionOut.user` — the caller's own record. */
export const userBriefSchema: z.ZodType<Schemas['UserBrief']> = z.object({
  id: z.string(),
  display_name: z.string(),
  email: z.string().nullable(),
  phone_e164: z.string().nullable(),
  status: z.string(),
  totp_enabled: z.boolean(),
});

/** `MeOut.tenant` — §5: `tenant{slug,name,timezone,status}`. */
export const tenantBriefSchema: z.ZodType<Schemas['TenantBrief']> = z.object({
  slug: z.string(),
  name: z.string(),
  timezone: z.string(),
  status: z.string(),
});

/** `POST /auth/login` 200. */
export const sessionOutSchema: z.ZodType<Schemas['SessionOut']> = z.object({
  user: userBriefSchema,
  roles: z.array(z.string()),
  expires_at: z.string(),
  csrf: z.string(),
  totp_enrolment_required: z.boolean(),
});

/** `GET /me` 200. BP-19: `locale` is already resolved by rule 1's chain. */
export const meOutSchema: z.ZodType<Schemas['MeOut']> = z.object({
  user: userBriefSchema,
  roles: z.array(z.string()),
  tenant: tenantBriefSchema,
  locale: z.string(),
});

/** `PUT /me/locale` 200 (BP-19 §5). */
export const localeOutSchema: z.ZodType<Schemas['LocaleOut']> = z.object({
  locale: z.string(),
  client_locale: z.string().nullable(),
});

/** `GET/PUT /tenant` 200. */
export const tenantOutSchema: z.ZodType<Schemas['TenantOut']> = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  domain: z.string(),
  timezone: z.string(),
  gstin: z.string().nullable(),
  status: z.string(),
  profile_hint: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** One row of `GET /users`, and the body of every other `/users` route. */
export const userOutSchema: z.ZodType<Schemas['UserOut']> = z.object({
  id: z.string(),
  email: z.string().nullable(),
  phone_e164: z.string().nullable(),
  display_name: z.string(),
  status: z.string(),
  roles: z.array(z.string()),
  totp_enabled: z.boolean(),
  last_login_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

/** `GET /users` 200 — one cursor page. */
export const userPageSchema: z.ZodType<Schemas['UserPage']> = z.object({
  items: z.array(userOutSchema),
  next_cursor: z.string().nullable(),
});

/** `POST /auth/totp/enrol` 200 (R7). */
export const totpEnrolOutSchema: z.ZodType<Schemas['TotpEnrolOut']> = z.object({
  otpauth_uri: z.string(),
  recovery_codes: z.array(z.string()),
});

/** `POST /auth/token` and `POST /auth/refresh` 200 (R6). */
export const tokenOutSchema: z.ZodType<Schemas['TokenOut']> = z.object({
  access: z.string(),
  refresh: z.string(),
  expires_in: z.number(),
});
